<script>
import Layout from '@layouts/main.vue'
import PageHeader from '@components/page-header.vue'

import { validateEmail } from '@utils/validation'
import { mapGetters, mapActions } from 'vuex'

export default {
  page: {
    title: 'Details',
    meta: [{ name: 'description', content: 'The Details page.' }],
  },
  components: { Layout, PageHeader },
  data: () => ({ emailAddressValid: true }),
  computed: {
    ...mapGetters({
      consent: 'payment/getConsent',
      emailAddress: 'payment/getEmailAddress',
    }),
  },
  beforeMount() {
    this.checkForData()
  },
  methods: {
    ...mapActions({
      setConsent: 'payment/setConsent',
      checkForData: 'payment/checkForData',
      setEmailAddress: 'payment/setEmailAddress',
    }),
    handleInput(e) {
      const emailAddress = e.target.value || null
      this.emailAddressValid = validateEmail(emailAddress)
      this.setEmailAddress({ emailAddress })
    },
    handleSubmit() {
      if (this.emailAddressValid) {
        this.$router.push('/payment').catch(() => {})
      }
    },
  },
}
</script>

<template>
  <Layout screen="details">
    <PageHeader :page="2" title="Your Details" />

    <div class="content">
      <div>
        <p
          ref="description"
          v-text="
            'Let us send you the receipt by entering your email below. We won\'t hang onto this, unless you fancy hearing from us.'
          "
        />
        <h2 ref="input" :class="$style.label" v-text="'Email'" />
        <form
          ref="form"
          :class="$style.validationInput"
          @submit.prevent="handleSubmit"
        >
          <BaseInputText
            type="email"
            :class="{ [$style.invalid]: !emailAddressValid }"
            :placeholder="'Your Email'"
            line
            :value="emailAddress"
            @input="handleInput"
          />
          <div v-if="!emailAddressValid" :class="$style.symbol" v-text="'!'" />
        </form>
        <div :class="$style.consent">
          <BaseCheckbox type="checkbox" name="consent" :checked="consent" />
          <label
            for="consent"
            :class="{ [$style.checked]: consent }"
            @click="setConsent({ consent: !consent })"
            v-text="
              ' Yes, I would like to receive offers, promotions and info on the latest happenings from The Alchemist via email.'
            "
          />
        </div>
      </div>
    </div>
    <div class="footer">
      <BaseButton
        :disabled="!emailAddress || !emailAddressValid"
        @click="handleSubmit"
        v-text="'Next'"
      />
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.container {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 65px);
  padding: 1.5rem 2.5rem;
  text-align: center;
  > * {
    width: 100%;
  }
}

.label {
  font-size: 1rem;
  padding: 1rem;
}

.invalid {
  border: var(--color-primary-disabled-border) 2px solid;
  background-color: var(--color-button-disabled-bg);
  outline: none;
}

.symbol {
  color: var(--color-primary);
  font-size: 1.25rem;
  position: absolute;
  right: 1rem;
  top: 0.5rem;
}

.validationInput {
  position: relative;
  width: 100%;
}

.consent {
  display: flex;
  margin: 24px 0;
  font-weight: bold;
  text-align: left;

  input {
    position: absolute;
    z-index: -1;
    opacity: 0;
  }

  label {
    margin-left: 16px;
    padding-left: 20px;
    position: relative;
    font-size: 12px;

    &:before,
    &:after {
      position: absolute;
      content: '';
    }

    &:before {
      width: 20px;
      height: 20px;
      border: var(--color-primary) 2px solid;
      background-color: #ffffff;
      left: -16px;
    }

    &:after {
      height: 0.3125rem;
      width: 0.75rem;
      border-left: 2px solid;
      border-bottom: 2px solid;
      color: #fff;

      transform: rotate(-53deg);

      left: -12px;
      top: 6px;
    }

    &.checked {
      &:before {
        background: var(--color-primary);
      }
    }
  }
}
</style>
